import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { MeResponseSchema, type Group, type MeResponse, type RegisterInput, type User } from '@sp/shared'
import { api } from './lib/api.ts'
import { HttpError, UNAUTHORIZED_EVENT } from './lib/http.ts'
import { disablePush } from './lib/push.ts'
import { keysWithPrefix, readJson, removeKey, writeJson } from './lib/storage.ts'

export type SessionState =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'signed-in'; me: MeResponse; offline: boolean }

interface SessionApi {
  state: SessionState
  login(email: string, password: string): Promise<void>
  register(input: RegisterInput): Promise<string | null>
  resetPassword(token: string, newPassword: string): Promise<void>
  /** Po odhlaseni zmizi i data v zarizeni, vcetne neodeslanych zmen. */
  logout(): Promise<void>
  /** Znovu nacte ucet a skupiny — po zmene clenstvi nebo nazvu. */
  refresh(): Promise<void>
  setMe(me: MeResponse): void
  groupId: string | null
  selectGroup(groupId: string): void
}

const SessionContext = createContext<SessionApi | null>(null)

const ME_KEY = 'sp:ja'
const GROUP_KEY = 'sp:skupina'
/** Clenstvi a jmena se meni zridka; pri navratu do appky staci jednou za minutu. */
const REFRESH_MS = 60_000

function cachedMe(): MeResponse | null {
  const parsed = MeResponseSchema.safeParse(readJson(ME_KEY))
  return parsed.success ? parsed.data : null
}

/** Vychozi skupina: prvni spolecna, osobni az kdyz jina neni. */
function pickGroup(me: MeResponse, preferred: string | null): string | null {
  if (preferred && me.groups.some((g) => g.id === preferred)) return preferred
  return me.groups.find((g) => !g.personal)?.id ?? me.groups[0]?.id ?? null
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: 'loading' })
  const [selected, setSelected] = useState<string | null>(() => {
    const stored = readJson(GROUP_KEY)
    return typeof stored === 'string' ? stored : null
  })
  const lastRefresh = useRef(0)

  // Vybrana skupina se odvozuje az v renderu a uklada v efektu. Zapis do
  // localStorage uvnitr updateru stavu bezel pozdeji nez vyber nove skupiny
  // a po obnoveni stranky vratil uzivatele do osobni skupiny.
  const me = state.status === 'signed-in' ? state.me : null
  const groupId = me ? pickGroup(me, selected) : selected
  useEffect(() => {
    if (groupId) writeJson(GROUP_KEY, groupId)
  }, [groupId])

  const applyMe = useCallback((next: MeResponse, offline = false) => {
    writeJson(ME_KEY, next)
    lastRefresh.current = Date.now()
    setState({ status: 'signed-in', me: next, offline })
  }, [])

  const goAnonymous = useCallback(() => {
    removeKey(ME_KEY)
    setState({ status: 'anonymous' })
  }, [])

  const refresh = useCallback(async () => {
    try {
      applyMe(await api.auth.me())
    } catch (err) {
      if (err instanceof HttpError && err.status === 401) goAnonymous()
      // Offline nebo vypadek serveru: zustava posledni znamy stav.
    }
  }, [applyMe, goAnonymous])

  useEffect(() => {
    let live = true
    api.auth
      .me()
      .then((fresh) => {
        if (live) applyMe(fresh)
      })
      .catch((err: unknown) => {
        if (!live) return
        // V obchode byva spatny signal. Kdyz server neni dosazitelny, ale uz
        // jsme se nekdy prihlasili, pust ji dal — jinak by misto seznamu
        // videla prihlasovaci obrazovku, kterou stejne neodesle.
        const known = cachedMe()
        const offline = err instanceof HttpError && (err.isOffline || err.status >= 500)
        if (offline && known) applyMe(known, true)
        else goAnonymous()
      })

    const onUnauthorized = () => goAnonymous()
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastRefresh.current > REFRESH_MS) {
        void refresh()
      }
    }
    const onOnline = () => void refresh()
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
    window.addEventListener('online', onOnline)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      live = false
      window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
      window.removeEventListener('online', onOnline)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [applyMe, goAnonymous, refresh])

  const login = useCallback(
    async (email: string, password: string) => applyMe(await api.auth.login({ email, password })),
    [applyMe],
  )

  const register = useCallback(
    async (input: RegisterInput) => {
      const { joinedGroupId, ...fresh } = await api.auth.register(input)
      if (joinedGroupId) setSelected(joinedGroupId)
      applyMe(fresh)
      return joinedGroupId
    },
    [applyMe],
  )

  const resetPassword = useCallback(
    async (token: string, newPassword: string) => applyMe(await api.auth.resetPassword(token, newPassword)),
    [applyMe],
  )

  const logout = useCallback(async () => {
    // Odber upozorneni patri uctu: po odhlaseni by na sdilenem tabletu chodily
    // akce odhlaseneho. Musi byt pred odhlasenim, server chce prihlaseni.
    await disablePush().catch(() => undefined)
    try {
      await api.auth.logout()
    } catch {
      // Bez signalu se aspon smaze stav v zarizeni; session na serveru vyprsi.
    }
    for (const key of keysWithPrefix('sp:')) removeKey(key)
    setSelected(null)
    setState({ status: 'anonymous' })
  }, [])

  const selectGroup = useCallback((id: string) => setSelected(id), [])

  const value = useMemo<SessionApi>(
    () => ({ state, login, register, resetPassword, logout, refresh, setMe: applyMe, groupId, selectGroup }),
    [state, login, register, resetPassword, logout, refresh, applyMe, groupId, selectGroup],
  )
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionApi {
  const ctx = useContext(SessionContext)
  if (!ctx) throw new Error('useSession mimo SessionProvider')
  return ctx
}

export interface SignedIn {
  user: User
  groups: Group[]
  /** Aktualne vybrana skupina. */
  group: Group
  offline: boolean
  selectGroup(groupId: string): void
  refresh(): Promise<void>
  setMe(me: MeResponse): void
  logout(): Promise<void>
  /** Jmeno clena podle id napric skupinami; null pro byvale cleny. */
  nameOf(userId: string | null): string | null
}

/** Jen uvnitr prihlasene casti appky (App.tsx to zarucuje). */
export function useSignedIn(): SignedIn {
  const session = useSession()
  const { state } = session
  if (state.status !== 'signed-in') throw new Error('useSignedIn bez prihlaseni')
  const { me } = state
  const group = me.groups.find((g) => g.id === session.groupId) ?? me.groups[0]
  if (!group) throw new Error('uzivatel bez skupiny')
  return {
    user: me.user,
    groups: me.groups,
    group,
    offline: state.offline,
    selectGroup: session.selectGroup,
    refresh: session.refresh,
    setMe: session.setMe,
    logout: session.logout,
    nameOf: (userId) => {
      if (!userId) return null
      if (userId === me.user.id) return me.user.name
      for (const g of me.groups) {
        const m = g.members.find((x) => x.userId === userId)
        if (m) return m.name
      }
      return null
    },
  }
}
